import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { api } from '@/api';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import { USERS_KEY } from '@/features/admin/api';
import { useCan } from '@/lib/permissions';
import { useMe } from '@/lib/session';
import { throwApiError } from '@/lib/server-messages';

/**
 * A "which of our people" field — the salesperson select's shape (SAL-01
 * §5), lifted out so a document can name a person in more than one role
 * (wave 5C: the goods receipt's Received by / Checked by).
 *
 * The offer rule: the company's ACTIVE people when the reader may list them
 * (`admin.view` opens /api/users); the reader themselves always, so a clerk
 * who cannot list the staff can still put their own name on the paper; and
 * whoever the record already names, by the name the server resolved, so a
 * person who has since left (or whom this reader cannot list) still DISPLAYS
 * — the offer rule governs new picks, not history.
 */
export function PersonSelect({
  id,
  label,
  value,
  heldName,
  onChange,
  disabled,
  emptyLabel,
}: {
  id: string;
  label: string;
  value: string;
  heldName: string | null;
  onChange: (userId: string) => void;
  disabled: boolean;
  /** The "nobody" row's text; the select always offers an empty choice. */
  emptyLabel: string;
}) {
  const { t } = useTranslation();
  const canList = useCan('admin.view');
  const { data: me } = useMe();
  const users = useQuery({
    queryKey: USERS_KEY,
    enabled: canList,
    queryFn: async () => {
      const response = await api.users.list();
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body.users;
    },
  });

  const options = new Map<string, string>();
  for (const user of users.data ?? []) {
    if (user.status === 'active') options.set(user.id, user.name || user.email);
  }
  if (me !== undefined && me !== null && !options.has(me.user.id)) {
    options.set(me.user.id, me.user.name || me.user.email);
  }
  if (value !== '' && !options.has(value)) options.set(value, heldName ?? value);

  return (
    <div className="grid gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <NativeSelect
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
        aria-label={label}
        // NFR-43: the labelled control is visible throughout; while the
        // people list is in flight it says so, and the held name still shows.
        aria-busy={users.isFetching || undefined}
        title={canList ? undefined : t('person.onlyYourself')}
      >
        <option value="">{emptyLabel}</option>
        {[...options.entries()].map(([userId, name]) => (
          <option key={userId} value={userId}>
            {name}
          </option>
        ))}
      </NativeSelect>
    </div>
  );
}
